import { z } from 'zod';

import { type MediaKind } from '../../media/media-kind/media-kind';

/**
 * The steps a case can be made of (`03-verification.md`).
 *
 * **Which of them a given case actually holds is not decided here** — it is
 * materialized once, at case creation, from the gender-scoped
 * `verification.steps` setting (U15). This list is the closed set of what may
 * exist; the setting decides what does.
 *
 * The order is the order a member meets them, and it is the order the checklist
 * renders in — evidence first, then the appointment, then the automated check
 * that depends on the evidence.
 */
export const VERIFICATION_STEP_KEYS = ['id_document', 'selfie', 'interview', 'face_match'] as const;

export const VerificationStepKeySchema = z.enum(VERIFICATION_STEP_KEYS);

export type VerificationStepKey = (typeof VERIFICATION_STEP_KEYS)[number];

const KEY_SET: ReadonlySet<string> = new Set<string>(VERIFICATION_STEP_KEYS);

/** Narrows a route parameter. Fail-closed: an unknown step has no rules at all. */
export function isVerificationStepKey(value: string): value is VerificationStepKey {
  return KEY_SET.has(value);
}

/**
 * The media kind a step's evidence is stored under, or `null` when the step is
 * not evidence at all.
 *
 * Declared here rather than derived from the matching names so that the two
 * registries stay explicitly linked (U3): the media kind policy is what
 * authorizes a reviewer to see the bytes, and a step whose kind was inferred
 * from a string would silently break the day either list is renamed.
 */
const EVIDENCE_KIND: Readonly<Record<VerificationStepKey, MediaKind | null>> = {
  id_document: 'id_document',
  selfie: 'selfie',
  interview: null,
  face_match: null,
};

export function evidenceKindForStep(step: VerificationStepKey): MediaKind | null {
  return EVIDENCE_KIND[step];
}

/**
 * Whether a member may submit this step through the evidence endpoint.
 *
 * `interview` is *requested*, not submitted — it has its own row and its own
 * route. `face_match` is computed by the server from evidence already sent, and
 * a member-facing write to it would be the subject of an advisory check editing
 * that check.
 */
export function isMemberSubmittableStep(step: VerificationStepKey): boolean {
  return EVIDENCE_KIND[step] !== null;
}
